/**
 * Reading the *reason* out of a refused request.
 *
 * The API sends RFC 9457 `application/problem+json` bodies on 401 and 403, carrying one of two
 * machine-readable codes so the UI can say why a write was refused rather than guessing. A response
 * whose body carries no code it recognizes degrades to "no reason given".
 *
 * Duck-typed on `{ status, error }` rather than importing `HttpErrorResponse` as a value, so the
 * spec can pass plain object literals — one case still uses a real `HttpErrorResponse` to prove the
 * shapes agree.
 */

/**
 * The problem member carrying the code — a top-level extension member, alongside `traceId`.
 *
 * Confirmed by the backend on 2026-08-19. It was a guess (`reason`) with a fallback that scanned the
 * body's other top-level strings for a known value; both are gone now that the name is known, because
 * a fallback nobody exercises is a fallback nobody notices breaking.
 */
export const PROBLEM_REASON_MEMBER = 'code';

/**
 * The codes the API sends. Matched exactly — these are identifiers, not user input.
 *
 * `missing_feature` covers **two** things the caller cannot distinguish anyway: the role-feature gate,
 * and the admin-only fields (a device's `deviceTypeId`, an organization's service level, minting a
 * `SystemAdministrator`). `org_out_of_scope` therefore means precisely "wrong organization" and
 * nothing else.
 */
export const DENIAL_REASONS = ['missing_feature', 'org_out_of_scope'] as const;
export type DenialReason = (typeof DENIAL_REASONS)[number];

/** The message key naming each reason, plus the one used when the response does not say. */
const DENIAL_MESSAGE_KEYS: Record<DenialReason, string> = {
  missing_feature:   'notify_denied_missing_feature',
  org_out_of_scope:  'notify_denied_org_out_of_scope',
};

/** Shown when a refusal carries no reason — today's empty 403, and any future unrecognized code. */
export const DENIAL_UNSPECIFIED_KEY = 'notify_denied_unspecified';

function isDenialReason(value: unknown): value is DenialReason {
  return typeof value === 'string' && (DENIAL_REASONS as readonly string[]).includes(value);
}

/** The parsed problem body, or null if there isn't one. Angular hands us either an object or raw text. */
function problemBody(error: unknown): Record<string, unknown> | null {
  if (typeof error !== 'object' || error === null) return null;
  const body = (error as { error?: unknown }).error;
  if (typeof body === 'string') {
    try {
      const parsed: unknown = JSON.parse(body);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : null;
    } catch {
      return null;
    }
  }
  return typeof body === 'object' && body !== null && !Array.isArray(body)
    ? body as Record<string, unknown>
    : null;
}

/** The statuses a denial reason may be read from. A denial is a refusal; a 400 is a rejected payload. */
export function isRefusal(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 401 || status === 403;
}

/**
 * What the API said was wrong with a rejected payload, or null when the response is not one.
 *
 * Server prose rather than a translation key, and deliberately: these name a rule the caller broke —
 * a serial number already in use, an IoT device already fitted to another appliance — and the set
 * grows every time a validator does, so a map maintained here would be a step behind the backend and
 * silently show nothing for the newest rule. The alternative to showing this line is showing no
 * reason at all, which is what sent testers looking for a permissions problem that was not there.
 *
 * Every message the response carries is joined into one line: `ValidationProblemDetails` keys them by
 * field, and a write refused on two fields owes the user both.
 */
export function validationMessagesOf(error: unknown): string | null {
  const status = (error as { status?: unknown } | null)?.status;
  if (status !== 400) return null;

  const body = problemBody(error);
  if (body === null) return null;

  const errors = body['errors'];
  if (typeof errors !== 'object' || errors === null || Array.isArray(errors)) return null;

  const messages: string[] = [];
  for (const fieldMessages of Object.values(errors as Record<string, unknown>)) {
    if (!Array.isArray(fieldMessages)) continue;
    for (const message of fieldMessages) {
      if (typeof message === 'string' && message.trim().length > 0)
        messages.push(message.trim());
    }
  }

  return messages.length > 0 ? messages.join(' ') : null;
}

/**
 * Why the request was refused, or null when it does not say — or was not a refusal at all.
 *
 * **Only 401 and 403.** A 400 `ValidationProblemDetails` is a different thing entirely and the invite
 * flow already maps it field by field; letting a 400 resolve to a denial here would put a
 * "your role does not allow this" line above a list of validation errors.
 */
export function denialReasonOf(error: unknown): DenialReason | null {
  if (!isRefusal(error)) return null;

  const body = problemBody(error);
  if (body === null) return null;

  const named = body[PROBLEM_REASON_MEMBER];
  return isDenialReason(named) ? named : null;
}

/** The translation key explaining a refusal. Always a key — an unreadable body yields the generic one. */
export function denialMessageKey(error: unknown): string {
  const reason = denialReasonOf(error);
  return reason === null ? DENIAL_UNSPECIFIED_KEY : DENIAL_MESSAGE_KEYS[reason];
}
